import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import { RateLimitedBulkActionExecutor } from "@/lib/hooks/api/shared/bulk-filter-types";
import { buildTicketFilterQuery, getFilteredTicketIds } from "./ticket-bulk-filter-utils";
import type {
  BulkActionProgressItem,
  BulkActionProgress,
  FilterBasedBulkActionParams,
} from "@/lib/hooks/api/shared/bulk-filter-types";
import type { TicketFilterCriteria } from "./ticket-bulk-filter-utils";

export function useBulkDeleteTicketsByFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      filterCriteria,
      batchSize = 50,
      delayMs = 1000,
      maxConcurrent = 3,
      onProgress,
    }: FilterBasedBulkActionParams<TicketFilterCriteria>) => {
      const tickets = await getFilteredTicketIds(filterCriteria);

      if (tickets.length === 0) {
        throw new Error("No tickets found matching the current filters");
      }

      const progressItems: BulkActionProgressItem[] = tickets.map((ticket) => ({
        id: ticket.id,
        name: ticket.title || `Ticket ${ticket.ticket_number || ticket.id}`,
        status: "pending",
        retryCount: 0,
      }));

      const executor = new RateLimitedBulkActionExecutor(maxConcurrent, delayMs);

      let completedCount = 0;
      let errorCount = 0;

      const updateProgress = () => {
        const progress: BulkActionProgress = {
          items: progressItems,
          totalCount: tickets.length,
          completedCount,
          errorCount,
          isComplete: completedCount + errorCount === tickets.length,
        };
        onProgress?.(progress);
      };

      await executor.executeBulkAction(
        tickets,
        async (ticket: { id: string; title: string }, index: number) => {
          const progressItem = progressItems[index];
          progressItem.status = "processing";
          updateProgress();

          try {
            const supabase = getSupabase();
            const { error } = await supabase.from("tickets").delete().eq("id", ticket.id);

            if (error) {
              throw new Error(error.message);
            }

            progressItem.status = "success";
            completedCount++;
          } catch (error) {
            progressItem.status = "error";
            progressItem.error = error instanceof Error ? error.message : "Unknown error";
            errorCount++;
          }

          updateProgress();
        }
      );

      return {
        totalProcessed: tickets.length,
        successful: completedCount,
        failed: errorCount,
        tickets: tickets.map((t) => t.id),
      };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
      void queryClient.invalidateQueries({ queryKey: ["tickets-paginated"] });

      if (result.failed === 0) {
        toast.success(`Successfully deleted ${result.successful} tickets`);
      } else {
        toast.warning(`Deleted ${result.successful} tickets, ${result.failed} failed`);
      }
    },
    onError: (error) => {
      toast.error("Failed to delete tickets");
      console.error("Error bulk deleting tickets by filter:", error);
    },
  });
}

export function useBulkExportTicketsByFilter() {
  return useMutation({
    mutationFn: async ({ filterCriteria }: { filterCriteria: TicketFilterCriteria }) => {
      const supabase = getSupabase();
      const query = buildTicketFilterQuery(supabase, filterCriteria);

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching tickets for export:", error);
        throw new Error(error.message);
      }

      if (!data || data.length === 0) {
        throw new Error("No tickets found matching the current filters");
      }

      const {
        objectsToCSV,
        downloadCSV,
        formatDateForCSV,
        formatCurrencyForCSV,
        formatNumberForCSV,
        formatArrayForCSV,
      } = await import("@/lib/utils/csv-export");

      const exportData = data.map((ticket: any) => ({
        "Ticket ID": ticket.id,
        "Ticket Number": ticket.ticket_number,
        Title: ticket.title || "",
        Description: ticket.description || "",
        Status: ticket.status || "",
        Priority: ticket.priority || "",
        Category: ticket.category || "",
        "Client Name": ticket.contact_name || "",
        "Client Company": ticket.company_name || "",
        "Assigned To": ticket.assigned_to || "",
        "Estimated Hours": formatNumberForCSV(ticket.estimated_hours),
        "Actual Hours": formatNumberForCSV(ticket.actual_hours),
        "Due Date": ticket.due_date ? formatDateForCSV(ticket.due_date) : "",
        "Resolved Date": ticket.resolved_at ? formatDateForCSV(ticket.resolved_at) : "",
        "Closed Date": ticket.closed_at ? formatDateForCSV(ticket.closed_at) : "",
        Tags: formatArrayForCSV(ticket.tags),
        "Internal Notes": ticket.internal_notes || "",
        "Customer Visible Notes": ticket.customer_visible_notes || "",
        "Created Date": formatDateForCSV(ticket.created_at),
        "Updated Date": formatDateForCSV(ticket.updated_at),
      }));

      const csvContent = objectsToCSV(exportData);
      const timestamp = new Date().toISOString().split("T")[0];
      downloadCSV(csvContent, `tickets_export_${timestamp}.csv`);

      return { exported: data.length };
    },
    onSuccess: (result) => {
      toast.success(`${result.exported} tickets exported successfully`);
    },
    onError: (error) => {
      toast.error("Failed to export tickets");
      console.error("Error exporting tickets:", error);
    },
  });
}
